<template>
  <a-drawer
    v-model:open="open"
    placement="right"
    :width="drawerWidth"
    :closable="false"
    :title="null"
    root-class-name="yt-user-drawer-root"
    class="yt-user-drawer"
    :body-style="{ padding: 0, height: '100%' }"
  >
    <div class="yt-user-menu">
      <div class="yt-user-menu__promo">
        <div class="yt-user-menu__promo-body">
          <span class="yt-user-menu__promo-icon" aria-hidden="true">
            <i class="fa-solid fa-crown" />
          </span>
          <div class="yt-user-menu__promo-text">
            <strong>{{ $t('header.vipVoucherTitle') }}</strong>
            <span>{{ $t('header.vipVoucherDesc') }}</span>
          </div>
        </div>
        <button
          type="button"
          class="yt-user-menu__promo-btn"
          @click="goMenu(DASHBOARD)"
        >
          {{ $t('header.buyNow') }}
        </button>
        <button
          type="button"
          class="yt-user-menu__close"
          :aria-label="$t('header.close')"
          @click="close"
        >
          <i class="fa-solid fa-xmark" />
        </button>
      </div>

      <div class="yt-user-menu__name">{{ userTagName }}</div>

      <button
        type="button"
        class="yt-user-menu__points"
        @click="goMenu(LOYALTY_POINTS)"
      >
        <span class="yt-user-menu__points-icon" aria-hidden="true">
          <i class="fa-solid fa-coins" />
        </span>
        <span class="yt-user-menu__points-meta">
          <strong>{{ balanceFull }} {{ $t('points.unit') }}</strong>
          <span>{{ $t('header.pointsTier', { tier: tierName }) }}</span>
        </span>
        <i class="fa-solid fa-chevron-right yt-user-menu__points-chevron" aria-hidden="true" />
      </button>

      <button
        type="button"
        class="yt-user-menu__checkin"
        @click="goMenu(LOGIN_DAILY_POINT)"
      >
        <i class="fa-solid fa-gift" aria-hidden="true" />
        <span>{{ $t('header.checkInClaim') }}</span>
        <i class="fa-solid fa-chevron-right yt-user-menu__checkin-chevron" aria-hidden="true" />
      </button>
      <div class="yt-user-menu__scroll">
        <div class="yt-user-menu__group">
          <button type="button" class="yt-user-menu__item" @click="goMenu(CUSTOMER_POST_NEWS)">
            <i class="fa-solid fa-arrows-rotate" aria-hidden="true" />
            <span>{{ $t('header.switchToPost') }}</span>
          </button>
        </div>

        <CustomerMenu @navigate="goMenu" @logout="logOut" />

        <div class="yt-user-menu__group">
          <button type="button" class="yt-user-menu__item" @click="goMenu(CUSTOMER_SETTING_ACCOUNT)">
            <i class="fa-solid fa-gear" aria-hidden="true" />
            <span>{{ $t('header.accountSettings') }}</span>
          </button>
          <button type="button" class="yt-user-menu__item" @click="goMenu(PASSWORD)">
            <i class="fa-solid fa-lock" aria-hidden="true" />
            <span>{{ $t('header.changePassword') }}</span>
          </button>
        </div>

        <div class="yt-user-menu__group yt-user-menu__group--last">
          <button type="button" class="yt-user-menu__item" @click="logOut">
            <i class="fa-solid fa-arrow-right-from-bracket" aria-hidden="true" />
            <span>{{ $t('text.logOut') }}</span>
          </button>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script setup lang="ts">
import { watch } from 'vue'
import router from '@/router'
import commons from '@/utils/Common'
import {
  CUSTOMER_POST_NEWS,
  CUSTOMER_SETTING_ACCOUNT,
  DASHBOARD,
  HOME,
  LOGIN_DAILY_POINT,
  LOYALTY_POINTS,
  PASSWORD,
} from '@/const/path'
import CustomerMenu from '@/components/Headers/default/customer/api/CustomerMenu.vue'
import { useLoyaltyPoints } from '@/composables/useLoyaltyPoints'

const props = withDefaults(
  defineProps<{
    userTagName?: string
  }>(),
  { userTagName: '' }
)

const open = defineModel<boolean>('open', { default: false })

const emit = defineEmits<{
  (e: 'logged-out'): void
}>()

const drawerWidth = 320
const { balanceFull, tierName, refresh } = useLoyaltyPoints()

watch(open, (val) => {
  if (val) refresh()
})

const close = () => {
  open.value = false
}

const goMenu = (path: string) => {
  open.value = false
  router.push({ path }).catch(() => undefined)
}

const logOut = () => {
  open.value = false
  commons.logOut()
  emit('logged-out')
  router.push({ path: HOME })
}
</script>

<style scoped>
.yt-user-menu {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  background: #fff;
}

.yt-user-menu__promo {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 16px 44px 16px 14px;
  background:
    linear-gradient(180deg, transparent 70%, rgba(229, 57, 53, 0.08) 100%),
    repeating-linear-gradient(
      -45deg,
      transparent,
      transparent 6px,
      rgba(229, 57, 53, 0.06) 6px,
      rgba(229, 57, 53, 0.06) 12px
    ),
    #ffe8e8;
}

.yt-user-menu__promo-body {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  min-width: 0;
}

.yt-user-menu__promo-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #e53935;
  color: #fff;
  font-size: 12px;
  flex: 0 0 auto;
}

.yt-user-menu__promo-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  color: #b71c1c;
}

.yt-user-menu__promo-text strong {
  font-size: 13px;
  font-weight: 700;
  line-height: 1.3;
}

.yt-user-menu__promo-text span {
  font-size: 11px;
  line-height: 1.35;
  opacity: 0.9;
}

.yt-user-menu__promo-btn {
  flex: 0 0 auto;
  height: 30px;
  padding: 0 12px;
  border: none;
  border-radius: 4px;
  background: #e53935;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
}

.yt-user-menu__promo-btn:hover {
  background: #c62828;
}

.yt-user-menu__close {
  position: absolute;
  top: 8px;
  right: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #b71c1c;
  font-size: 14px;
  cursor: pointer;
}

.yt-user-menu__close:hover {
  background: rgba(183, 28, 28, 0.1);
}

.yt-user-menu__name {
  padding: 12px 16px 8px;
  background: #f7f7f7;
  color: #0f0f0f;
  font-size: 14px;
  font-weight: 700;
}

.yt-user-menu__points {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 16px 14px;
  border: none;
  border-bottom: 1px solid #eee;
  background: #f7f7f7;
  color: #0f0f0f;
  text-align: left;
  cursor: pointer;
}

.yt-user-menu__points:hover {
  background: #f0f0f0;
}

.yt-user-menu__points-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: linear-gradient(145deg, #f59e0b, #d97706);
  color: #fff;
  font-size: 16px;
  flex: 0 0 auto;
  box-shadow: 0 4px 10px rgba(217, 119, 6, 0.28);
}

.yt-user-menu__points-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.yt-user-menu__points-meta strong {
  font-size: 16px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #111;
}

.yt-user-menu__points-meta span {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
}

.yt-user-menu__points-chevron {
  font-size: 11px;
  color: #9ca3af;
}

.yt-user-menu__checkin {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 16px;
  border: none;
  border-bottom: 1px solid #eee;
  background: linear-gradient(90deg, #fff8e8 0%, #fff 100%);
  color: #8a5a00;
  font-size: 14px;
  font-weight: 700;
  text-align: left;
  cursor: pointer;
}

.yt-user-menu__checkin:hover {
  background: linear-gradient(90deg, #ffefc8 0%, #fff8e8 100%);
}

.yt-user-menu__checkin > .fa-gift {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: #f59e0b;
  color: #fff;
  font-size: 12px;
  flex: 0 0 auto;
}

.yt-user-menu__checkin > span {
  flex: 1;
  min-width: 0;
}

.yt-user-menu__checkin-chevron {
  margin-left: auto;
  font-size: 11px;
  color: rgba(138, 90, 0, 0.55);
}

.yt-user-menu__scroll {
  flex: 1 1 auto;
  overflow: auto;
}

.yt-user-menu__group {
  padding: 6px 0;
  border-bottom: 1px solid #eee;
}

.yt-user-menu__group--last {
  border-bottom: none;
}

.yt-user-menu__item {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 11px 16px;
  border: none;
  background: transparent;
  color: #222;
  font-size: 14px;
  text-align: left;
  cursor: pointer;
}

.yt-user-menu__item i {
  width: 18px;
  color: #444;
  font-size: 14px;
  text-align: center;
  flex: 0 0 auto;
}

.yt-user-menu__item span {
  flex: 1 1 auto;
  min-width: 0;
}

.yt-user-menu__item:hover {
  background: #f2f2f2;
}

.yt-user-menu__badge {
  flex: 0 0 auto !important;
  margin-left: auto;
  padding: 2px 8px;
  border-radius: 4px;
  background: #e0f7fa;
  color: #00838f;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
</style>

<style>
.yt-user-drawer-root .ant-drawer-content-wrapper {
  max-width: 100vw;
}

.yt-user-drawer-root .ant-drawer-content {
  background: #fff !important;
}

.yt-user-drawer-root .ant-drawer-header {
  display: none;
}

.yt-user-drawer-root .ant-drawer-body {
  padding: 0 !important;
  height: 100%;
}

.yt-user-drawer-root .ant-drawer-mask {
  background: rgba(0, 0, 0, 0.35) !important;
}
</style>
